import {
  CURRICULUM_KEYS,
  type CurriculumCore,
  type CurriculumCoreNodes,
  NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS,
  removeRevisionKoFromLabel,
  type TrieKey,
} from '@mathflat/domain/Curriculum'
import type {
  AllGradeSubject,
  GradeElementaryMiddle,
  Revision,
  SchoolType,
} from '@mathflat/domain/SchoolSystem'
import { makeAutoObservable } from 'mobx'

import { curriculumController } from '@/controller/curriculum.controller'
import type commonRepo from '@/repository/common.repo'
import {
  type AllTypeCurriculumCoreItem,
  type AllTypeCurriculumCoreSchool,
  makeAllTypeCurriculumCoreItem,
  makeAllTypeCurriculumCoreSchool,
  makeUnionGradeCurriculumCoreItem,
  type SubscribableCurriculumCoreItem,
  type SubscribableCurriculumCoreSchool,
  UNION_CURRICULUM_KEY,
  type UnionGradeCurriculumCoreItem,
} from '@/service/curriculumCore.model'

export type CoreItem = SubscribableCurriculumCoreItem
export type UnionCoreItem = UnionGradeCurriculumCoreItem
type AllTypeCoreItem = AllTypeCurriculumCoreItem

type ExpendUnionCoreItem = UnionCoreItem | AllTypeCoreItem
export type ExpendCoreItem = CoreItem | AllTypeCoreItem

export type ExpandedGradeSemesterSubject = ExpendUnionCoreItem | ExpendCoreItem

type Params = {
  includeAll?: boolean
  includePreOpen?: boolean
  expandedLabel?: string
  revisions?: Revision[]
}

const defaultParams = {
  includeAll: true,
  includePreOpen: true,
  revisions: ['CURRICULUM_15', 'CURRICULUM_22'],
} as const satisfies Params

abstract class BaseCurriculumCoreService {
  protected _schoolTypes: CurriculumCoreNodes['schoolTypes'] = []
  protected _revisions: CurriculumCoreNodes['revisions'] = []
  protected _schools: SubscribableCurriculumCoreSchool[] = []
  // trieKey와 커리큘럼이 1:1로 매칭되는 유니크한 커리큘럼
  protected _curriculumMap: Map<TrieKey, CoreItem> = new Map()
  // 여러개가 조합된 trieKey와 해당 커리큘럼들의 공통만 뽑은 통합 커리큘럼(개정 통합 혹은 고등학교의 경우 학년으로 치환)
  protected get _unionCurriculumMap() {
    const result = new Map<TrieKey, UnionGradeCurriculumCoreItem>()

    const groupMap = new Map<string, CoreItem[]>()

    this._curriculumMap.forEach((item) => {
      // 고등학교: schoolType, categorizedGrade
      if (item.schoolType === 'HIGH') {
        const grade = item.categorizedGrade
        if (!grade) return
        const key = `${item.schoolType}-${grade}`
        if (!groupMap.has(key)) groupMap.set(key, [])
        groupMap.get(key)!.push(item)
      } else {
        // 초/중: schoolType, gradeSubject, semester
        const key = `${item.schoolType}-${item.gradeSubject}-${item.semester ?? ''}`
        if (!groupMap.has(key)) groupMap.set(key, [])
        groupMap.get(key)!.push(item)
      }
    })

    groupMap.forEach((items) => {
      if (items.length > 0) {
        const unionItem = makeUnionGradeCurriculumCoreItem(items)
        result.set(unionItem.trieKey, unionItem)
      }
    })

    return result
  }

  protected get _gradeSubjects(): Record<SchoolType, CoreItem[]> {
    return {
      ELEMENTARY: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'ELEMENTARY' && !item.semester && item.status !== 'HIDDEN',
      ),
      MIDDLE: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'MIDDLE' && !item.semester && item.status !== 'HIDDEN',
      ),
      HIGH: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'HIGH' && item.status !== 'HIDDEN',
      ),
    }
  }

  protected get _gradeSemesterSubjects(): Record<SchoolType, CoreItem[]> {
    return {
      ELEMENTARY: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'ELEMENTARY' && item.semester && item.status !== 'HIDDEN',
      ),
      MIDDLE: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'MIDDLE' && item.semester && item.status !== 'HIDDEN',
      ),
      HIGH: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'HIGH' && item.status !== 'HIDDEN',
      ),
    }
  }

  protected get _unionGradeSubjects(): Record<SchoolType, UnionCoreItem[]> {
    return {
      ELEMENTARY: Array.from(this._unionCurriculumMap.values()).filter(
        (item) => item.schoolType === 'ELEMENTARY' && !item.semester,
      ),
      MIDDLE: Array.from(this._unionCurriculumMap.values()).filter(
        (item) => item.schoolType === 'MIDDLE' && !item.semester,
      ),
      HIGH: Array.from(this._unionCurriculumMap.values()).filter(
        (item) => item.schoolType === 'HIGH',
      ),
    }
  }

  protected get _unionGradeSemesterSubjects(): Record<SchoolType, UnionCoreItem[]> {
    return {
      ELEMENTARY: Array.from(this._unionCurriculumMap.values()).filter(
        (item) => item.schoolType === 'ELEMENTARY' && item.semester,
      ),
      MIDDLE: Array.from(this._unionCurriculumMap.values()).filter(
        (item) => item.schoolType === 'MIDDLE' && item.semester,
      ),
      HIGH: Array.from(this._unionCurriculumMap.values()).filter(
        (item) => item.schoolType === 'HIGH',
      ),
    }
  }

  constructor() {}

  init = (curriculum: CurriculumCore) => {
    const { revisions, schoolTypes, schools, grades, semesters, subjects } = curriculum
    this._revisions = revisions
    this._schoolTypes = schoolTypes
    this._schools = schools.map((school) => ({ ...school, subscribeStatus: 'SUBSCRIBED' }))
    ;[...grades, ...semesters, ...subjects].forEach((item) => {
      this._curriculumMap.set(item.trieKey, { ...item, subscribeStatus: 'SUBSCRIBED' })
    })
  }

  private _filterByRevision = (targetRevision: Revision, revisions: Revision[]) => {
    return revisions.includes(targetRevision)
  }
  private _filterByPreOpenInclude = (includePreOpen: boolean, item: UnionCoreItem | CoreItem) => {
    if (includePreOpen) return true
    return item.status !== 'PRE_OPEN'
  }

  getAllTypeItem({
    schoolType,
    expandedLabel,
  }: {
    schoolType?: SchoolType
    expandedLabel?: string
  }): AllTypeCoreItem | undefined {
    if (schoolType) {
      return makeAllTypeCurriculumCoreItem({
        schoolType,
        label: expandedLabel,
      })
    } else {
      return makeAllTypeCurriculumCoreItem({
        schoolType: null,
        label: expandedLabel,
      })
    }
  }

  protected _getCurriculumArray({
    type = 'GRADE',
    includeAll = defaultParams.includeAll,
    includePreOpen = defaultParams.includePreOpen,
    revisions = defaultParams.revisions,
    expandedLabel,
    schoolType = 'ELEMENTARY',
  }: Params & { schoolType: SchoolType; type: 'GRADE' | 'SEMESTER' }) {
    let _gradeSubject: CoreItem[] = []
    switch (schoolType) {
      case 'ELEMENTARY':
        _gradeSubject =
          type === 'SEMESTER'
            ? this._gradeSemesterSubjects.ELEMENTARY
            : this._gradeSubjects.ELEMENTARY
        break
      case 'MIDDLE':
        _gradeSubject =
          type === 'SEMESTER' ? this._gradeSemesterSubjects.MIDDLE : this._gradeSubjects.MIDDLE
        break
      case 'HIGH':
        _gradeSubject = this._gradeSubjects.HIGH
        break
    }

    _gradeSubject =
      _gradeSubject.filter((item) => {
        return (
          this._filterByRevision(item.revision || 'CURRICULUM_15', revisions) &&
          this._filterByPreOpenInclude(includePreOpen, item)
        )
      }) || []

    if (includeAll) {
      const ALL_TYPE_ITEM = this.getAllTypeItem({ schoolType, expandedLabel })
      return ALL_TYPE_ITEM ? [ALL_TYPE_ITEM, ..._gradeSubject] : _gradeSubject
    } else {
      return _gradeSubject
    }
  }

  protected _getUnionCurriculumArray({
    type = 'SEMESTER',
    includeAll = defaultParams.includeAll,
    includePreOpen = defaultParams.includePreOpen,
    expandedLabel,
    schoolType = 'ELEMENTARY',
  }: Params & { schoolType: SchoolType; type: 'SEMESTER' | 'GRADE' }) {
    let _gradeSubject: UnionCoreItem[] = []
    switch (schoolType) {
      case 'ELEMENTARY':
        _gradeSubject =
          type === 'SEMESTER'
            ? this._unionGradeSemesterSubjects.ELEMENTARY
            : this._unionGradeSubjects.ELEMENTARY
        break
      case 'MIDDLE':
        _gradeSubject =
          type === 'SEMESTER'
            ? this._unionGradeSemesterSubjects.MIDDLE
            : this._unionGradeSubjects.MIDDLE
        break
      case 'HIGH':
        _gradeSubject = this._unionGradeSubjects.HIGH ?? []
        break
    }
    _gradeSubject =
      _gradeSubject.filter((item) => this._filterByPreOpenInclude(includePreOpen, item)) || []

    if (includeAll) {
      const ALL_TYPE_ITEM = this.getAllTypeItem({ schoolType, expandedLabel })
      return ALL_TYPE_ITEM ? [ALL_TYPE_ITEM, ..._gradeSubject] : _gradeSubject
    } else {
      return _gradeSubject
    }
  }

  get preOpenItems() {
    return Array.from(this._curriculumMap.values()).filter((item) => item.status === 'PRE_OPEN')
  }
  // 개정 ======================================================================================================
  getRevisionInfo = (revision: Revision) => {
    return this._revisions.find((item) => item.revision === revision)
  }
  // 학교 ===================================================
  // 초등/중/고등 학교의 trieKey 조회
  getSchoolsTrieKeys(schoolType: SchoolType) {
    return this._schools.find((item) => item.value === schoolType)?.trieKeys || []
  }
  // 학교 타입
  getSchools({ includeAll = true }: Pick<Params, 'includeAll'>) {
    return includeAll ? [makeAllTypeCurriculumCoreSchool(), ...this._schools] : this._schools
  }
  /** trieKey로 학년, 학년학기 정보 조회 */
  getInfoByTrieKey = (trieKey: TrieKey) => {
    const _targetArray = trieKey.includes(UNION_CURRICULUM_KEY)
      ? this._unionCurriculumMap
      : this._curriculumMap

    const item = _targetArray.get(trieKey)

    if (!item) {
      throw new Error('유효하지 않은 trieKey')
    }

    return item
  }

  // 초등학교 ======================================================================================================
  /** 초등학교 학년 */
  getGradeElementary(params: Params = defaultParams) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'ELEMENTARY',
      expandedLabel: params.expandedLabel || '학년 전체',
      type: 'GRADE',
    })
  }
  /** 초등학교 학년-학기 */
  getGradeSemesterElementary(params: Params = defaultParams) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'ELEMENTARY',
      type: 'SEMESTER',
    })
  }
  /** 통합 초등학교 학년 */
  getUnionGradeElementary(params: Params = defaultParams) {
    return this._getUnionCurriculumArray({
      ...params,
      schoolType: 'ELEMENTARY',
      type: 'GRADE',
    })
  }
  /** 통합 초등학교 학년-학기 */
  getUnionGradeSemesterElementary(params: Params = defaultParams) {
    return this._getUnionCurriculumArray({
      ...params,
      schoolType: 'ELEMENTARY',
      type: 'SEMESTER',
    })
  }
  // 중학교 ======================================================================================================
  /** 중학교 학년 */
  getGradeMiddle(params: Params = defaultParams) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'MIDDLE',
      expandedLabel: params.expandedLabel || '학년 전체',
      type: 'GRADE',
    })
  }
  /** 중학교 학년 - 학기 */
  getGradeSemesterMiddle(params: Params = defaultParams) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'MIDDLE',
      type: 'SEMESTER',
    })
  }
  /** 통합 중학교 학년 */
  getUnionGradeMiddle(params: Params = defaultParams) {
    return this._getUnionCurriculumArray({
      ...params,
      schoolType: 'MIDDLE',
      type: 'GRADE',
    })
  }
  /** 통합 중학교 학기 */
  getUnionGradeSemesterMiddle(params: Params = defaultParams) {
    return this._getUnionCurriculumArray({
      ...params,
      schoolType: 'MIDDLE',
      type: 'SEMESTER',
    })
  }
  // 고등학교 ======================================================================================================
  /** 고등학교 과목 */
  getGradeSubjectHigh(params: Params = defaultParams) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'HIGH',
      type: 'GRADE',
    })
  }
  /** 정규 교육과정 외 고등학교 과목 조회 */
  getNonStandardGradeSubjectHigh({
    trieKeys,
  }: {
    trieKeys: ValueOf<typeof NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS>[]
  }) {
    return trieKeys.map((trieKey) => {
      if (!Object.values(NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS).includes(trieKey)) {
        console.error('trieKeys are not in NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS')
      }
      return this._curriculumMap.get(trieKey) as SubscribableCurriculumCoreItem<'HIGH'>
    })
  }
  /** 통합 고1,2,3학년(고등학교는 학기 없음) */
  getUnionGradeHigh(params: Params = defaultParams) {
    return this._getUnionCurriculumArray({
      ...params,
      schoolType: 'HIGH',
      type: 'GRADE',
    })
  }
}

class _DefaultCurriculumService extends BaseCurriculumCoreService {
  constructor() {
    super()
  }
}

class _SoloCurriculumService extends BaseCurriculumCoreService {
  private _subscribedItems: NonNullable<
    typeof commonRepo.permission.bySubscription.솔로_구독_커리큘럼
  > = []
  constructor() {
    super()
  }

  override get _unionCurriculumMap() {
    // 정책상 솔로구독 학년을 우선시 하므로 _flatUnionGradeColl에 학년구독 기반으로 UNSUBSCRIBED overwrite
    const _flatUnionGradeColl = super._unionCurriculumMap
    const _subscribedSchoolAndGrades =
      this._subscribedItems?.reduce(
        (acc, item) => {
          if (item.schoolType && item.grade) {
            acc.push([item.schoolType, item.grade])
          }
          return acc
        },
        [] as [SchoolType, GradeElementaryMiddle][],
      ) || []

    _flatUnionGradeColl.forEach((item) => {
      if (
        !_subscribedSchoolAndGrades.some(
          ([schoolType, grade]) => item.schoolType === schoolType && item.gradeSubject === grade,
        )
      ) {
        item.subscribeStatus = 'UNSUBSCRIBED'
      }
    })
    return _flatUnionGradeColl
  }

  override init = (
    curriculum: CurriculumCore,
    subscribedCurriculums?: typeof commonRepo.permission.bySubscription.솔로_구독_커리큘럼,
  ) => {
    this._subscribedItems = subscribedCurriculums || []

    const _subscribedSchoolTypes = this._subscribedItems?.map((item) => item.schoolType) || []
    const _subscribedTrieKeys = this._subscribedItems?.flatMap((item) => item.trieKeys) || []

    const { revisions, schoolTypes, schools, grades, semesters, subjects } = curriculum

    this._revisions = revisions
    this._schoolTypes = schoolTypes

    // solo set
    this._schools = schools.map((item) => ({
      ...item,
      subscribeStatus: _subscribedSchoolTypes.includes(item.value) ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
    }))

    grades.forEach((grade) => {
      const isSubscribed = !!this._subscribedItems?.some(
        (item) => grade.schoolType === item.schoolType && grade.gradeSubject === String(item.grade),
      )
      this._curriculumMap.set(grade.trieKey, {
        ...grade,
        subscribeStatus: isSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
      })
    })

    semesters.forEach((semester) => {
      const isSubscribed =
        _subscribedTrieKeys.includes(semester.trieKey) ||
        // trieKey 매칭 실패 시 학년 기반 fallback (subscribeItems에 trieKeys가 없거나 22개정 trieKey가 누락된 경우 대비)
        !!this._subscribedItems?.some(
          (item) =>
            semester.schoolType === item.schoolType && semester.gradeSubject === String(item.grade),
        )
      this._curriculumMap.set(semester.trieKey, {
        ...semester,
        subscribeStatus: isSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
      })
    })

    subjects.forEach((subject) => {
      this._curriculumMap.set(subject.trieKey, {
        ...subject,
        subscribeStatus: _subscribedTrieKeys.includes(subject.trieKey)
          ? 'SUBSCRIBED'
          : 'UNSUBSCRIBED',
      })
    })
  }
  // 솔로에서는 늘 getAllTypeItem을 null & includeAll: false로 처리하여 늘 전체아이템이 없도록 함
  override getAllTypeItem() {
    return undefined
  }
  override _getCurriculumArray({
    type = 'GRADE',
    includePreOpen = defaultParams.includePreOpen,
    revisions = defaultParams.revisions,
    expandedLabel,
    schoolType = 'ELEMENTARY',
  }: Params & { schoolType: SchoolType; type: 'GRADE' | 'SEMESTER' }) {
    return super._getCurriculumArray({
      type,
      includePreOpen,
      revisions,
      expandedLabel,
      schoolType,
      includeAll: false,
    })
  }

  override _getUnionCurriculumArray({
    type = 'SEMESTER',
    includePreOpen = defaultParams.includePreOpen,
    expandedLabel,
    schoolType = 'ELEMENTARY',
  }: Params & { schoolType: SchoolType; type: 'SEMESTER' | 'GRADE' }) {
    return super._getUnionCurriculumArray({
      type,
      includePreOpen,
      expandedLabel,
      schoolType,
      includeAll: false,
    })
  }

  // 학교급 ======================================================================================================
  override getSchools = () => {
    return super.getSchools({ includeAll: false })
  }
}
export class CurriculumCoreService {
  private _curriculum: _DefaultCurriculumService | _SoloCurriculumService
  private _isInit = false
  constructor() {
    makeAutoObservable(this)
    this._curriculum = new _DefaultCurriculumService()
  }

  fetchSetCurriculum = async ({
    plan,
    subscribedCurriculums,
  }: {
    plan?: 'default' | 'solo'
    subscribedCurriculums?: typeof commonRepo.permission.bySubscription.솔로_구독_커리큘럼
  }) => {
    const response = await curriculumController.fetchCurriculumCore()
    switch (plan) {
      case 'solo':
        this._curriculum = new _SoloCurriculumService()
        break
      default:
        this._curriculum = new _DefaultCurriculumService()
        break
    }
    this._curriculum.init(response, subscribedCurriculums)
    this._isInit = true
  }

  get isInit() {
    return this._isInit
  }

  // 공통 ======================================================================================================
  get preOpenItems() {
    return this._curriculum.preOpenItems
  }
  // trieKey로 학년, 학년-학기 정보 조회
  getUnionItemByUnionTrieKey(
    trieKey: Parameters<BaseCurriculumCoreService['getInfoByTrieKey']>[0],
  ) {
    if (trieKey === '') {
      console.error('trieKey is empty')
      return
    }
    if (trieKey === 'ALL') {
      console.error('trieKey is ALL : 사용부에서 All check 필요')
      return
    }
    return this._curriculum.getInfoByTrieKey(trieKey) as UnionCoreItem
  }

  getItemByTrieKey(trieKey: Parameters<BaseCurriculumCoreService['getInfoByTrieKey']>[0]) {
    if (trieKey === '') {
      console.error('trieKey is empty')
      return
    }
    if (trieKey === 'ALL') {
      console.error('trieKey is ALL : 사용부에서 All check 필요')
      return
    }
    return this._curriculum.getInfoByTrieKey(trieKey) as CoreItem
  }

  getUnionItemByGradeSemesterInfo({
    schoolType,
    gradeSubject,
    semester,
  }: {
    schoolType: SchoolType
    gradeSubject: AllGradeSubject
    semester?: UnionCoreItem['semester']
  }) {
    let _item: UnionCoreItem | undefined
    if (semester) {
      _item = [
        ...this.getUnionGradeSemesterElementary({ includeAll: false }),
        ...this.getUnionGradeSemesterMiddle({ includeAll: false }),
        ...this.getUnionGradeHigh({ includeAll: false }),
      ].find(
        (item) =>
          item.schoolType === schoolType &&
          item.gradeSubject === gradeSubject &&
          item.semester === semester,
      )
    } else {
      _item = [
        ...this.getUnionGradeElementary({ includeAll: false }),
        ...this.getUnionGradeMiddle({ includeAll: false }),
        ...this.getUnionGradeHigh({ includeAll: false }),
      ].find((item) => item.schoolType === schoolType && item.gradeSubject === gradeSubject)
    }

    return _item
  }
  private _findItemByGradeSemesterInfo({
    revisions,
    schoolType,
    gradeSubject,
    semester,
  }: {
    revisions?: Revision[]
    schoolType: SchoolType
    gradeSubject: CoreItem['gradeSubject']
    semester?: CoreItem['semester']
  }): SubscribableCurriculumCoreItem<SchoolType> | undefined {
    const _revisions = revisions || ['CURRICULUM_15', 'CURRICULUM_22']

    if (schoolType !== 'HIGH' && semester) {
      return this.getAllGradeSemesters({ revisions: _revisions }).find(
        (item) =>
          item.schoolType === schoolType &&
          item.gradeSubject === gradeSubject &&
          item.semester === semester,
      )
    } else {
      return this.getAllGrades({ revisions: _revisions }).find((item) => {
        // 테마관 학습지등 일부 경우 HIGH의 gradeSubject가 '1'~'3'이 들어올 수 있다.
        if (item.schoolType === 'HIGH' && Number.isInteger(Number(gradeSubject))) {
          return item.schoolType === schoolType && item.categorizedGrade === gradeSubject
        }
        return item.schoolType === schoolType && item.gradeSubject === gradeSubject
      })
    }
  }

  getItemByGradeSemesterInfo({
    revisions,
    schoolType,
    gradeSubject,
    semester,
  }: {
    revisions?: Revision[]
    schoolType: SchoolType
    gradeSubject: CoreItem['gradeSubject']
    semester?: CoreItem['semester']
  }) {
    const _item = this._findItemByGradeSemesterInfo({
      revisions,
      schoolType,
      gradeSubject,
      semester,
    })

    if (!_item) {
      throw new Error('item not found')
    }
    return _item
  }
  // 학생의 학년/학기 정보로 기본 커리큘럼key 가져오기(고등학생은 고1,고2,고3 처럼 학년 기반 이어서 기본 값 처리 해줘야 함)
  getDefaultTrieKeyBySchoolGrade({
    grade,
    schoolType,
    semester,
    revision,
  }: Pick<CoreItem, 'schoolType' | 'semester'> & {
    grade: CoreItem['gradeSubject']
    revision?: CoreItem['revision']
  }) {
    if (schoolType === 'HIGH') {
      const date = new Date()
      const currentMonth = date.getMonth()
      const semesterByMonth = currentMonth <= 5 ? 1 : 2
      if (grade === '1') {
        return semesterByMonth === 1
          ? CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상
          : CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_하
      } else if (grade === '2' || grade === '3') {
        return semesterByMonth === 1
          ? CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학I
          : CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학II
      }
    }

    const list =
      schoolType === 'ELEMENTARY'
        ? this.getGradeSemesterElementary({
            includeAll: false,
          })
        : this.getGradeSemesterMiddle({
            includeAll: false,
          })

    return list.find(
      (info) =>
        info.gradeSubject === grade &&
        (semester ? info.semester === semester : true) &&
        (revision ? info.revision === revision : true),
    )?.trieKey
  }

  isPreOpenCurriculum({
    revisions,
    schoolType,
    gradeSubject,
    semester,
  }: {
    revisions?: Revision[]
    schoolType: SchoolType
    gradeSubject: CoreItem['gradeSubject']
    semester?: CoreItem['semester']
  }) {
    const _item = this._findItemByGradeSemesterInfo({
      revisions,
      schoolType,
      gradeSubject,
      semester,
    })

    if (!_item) {
      return false
    }

    return _item.status === 'PRE_OPEN'
  }

  // 모든 학년
  getAllGrades(
    {
      includePreOpen = defaultParams.includePreOpen,
      revisions = defaultParams.revisions,
    }: Pick<Params, 'includePreOpen' | 'revisions'> = {
      includePreOpen: defaultParams.includePreOpen,
      revisions: defaultParams.revisions,
    },
  ) {
    return [
      ...this.getGradeElementary({ includeAll: false, includePreOpen, revisions }),
      ...this.getGradeMiddle({ includeAll: false, includePreOpen, revisions }),
      ...this.getGradeSubjectHigh({ includeAll: false, includePreOpen, revisions }),
    ]
  }
  // 모든 학년-학기
  getAllGradeSemesters(
    {
      includePreOpen = defaultParams.includePreOpen,
      revisions = defaultParams.revisions,
    }: Pick<Params, 'includePreOpen' | 'revisions'> = {
      includePreOpen: defaultParams.includePreOpen,
      revisions: defaultParams.revisions,
    },
  ): SubscribableCurriculumCoreItem[] {
    return [
      ...this.getGradeSemesterElementary({ includeAll: false, includePreOpen, revisions }),
      ...this.getGradeSemesterMiddle({ includeAll: false, includePreOpen, revisions }),
      ...this.getGradeSubjectHigh({ includeAll: false, includePreOpen, revisions }),
    ]
  }

  // 개정 ======================================================================================================
  getRevisionInfo(params: Parameters<BaseCurriculumCoreService['getRevisionInfo']>[0]) {
    return this._curriculum.getRevisionInfo(params)
  }
  // 학교 ======================================================================================================
  getSchoolsTrieKeys(schoolType: SchoolType) {
    return this._curriculum.getSchoolsTrieKeys(schoolType)
  }

  getSchools<T extends boolean>(
    params: Omit<Parameters<BaseCurriculumCoreService['getSchools']>[0], 'includeAll'> & {
      includeAll?: T
    } = { includeAll: true as T },
  ) {
    return this._curriculum.getSchools(params) as T extends true
      ? (AllTypeCurriculumCoreSchool | SubscribableCurriculumCoreSchool)[]
      : SubscribableCurriculumCoreSchool[]
  }

  // 초등학교 ======================================================================================================
  getGradeElementary<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getGradeElementary']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getGradeElementary(params) as T extends true
      ? (AllTypeCurriculumCoreItem | SubscribableCurriculumCoreItem<'ELEMENTARY'>)[]
      : SubscribableCurriculumCoreItem<'ELEMENTARY'>[]
  }

  getGradeSemesterElementary<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getGradeSemesterElementary']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getGradeSemesterElementary(params) as T extends true
      ? (AllTypeCurriculumCoreItem | SubscribableCurriculumCoreItem<'ELEMENTARY'>)[]
      : SubscribableCurriculumCoreItem<'ELEMENTARY'>[]
  }

  getUnionGradeElementary<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getUnionGradeElementary']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getUnionGradeElementary(params) as T extends true
      ? (UnionGradeCurriculumCoreItem<'ELEMENTARY'> | AllTypeCurriculumCoreItem)[]
      : UnionGradeCurriculumCoreItem<'ELEMENTARY'>[]
  }

  getUnionGradeSemesterElementary<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getUnionGradeSemesterElementary']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getUnionGradeSemesterElementary(params) as T extends true
      ? (UnionGradeCurriculumCoreItem<'ELEMENTARY'> | AllTypeCurriculumCoreItem)[]
      : UnionGradeCurriculumCoreItem<'ELEMENTARY'>[]
  }

  // 중학교 ======================================================================================================
  getGradeMiddle<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getGradeMiddle']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getGradeMiddle(params) as T extends true
      ? (AllTypeCurriculumCoreItem | SubscribableCurriculumCoreItem<'MIDDLE'>)[]
      : SubscribableCurriculumCoreItem<'MIDDLE'>[]
  }
  getGradeSemesterMiddle<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getGradeSemesterMiddle']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getGradeSemesterMiddle(params) as T extends true
      ? (AllTypeCurriculumCoreItem | SubscribableCurriculumCoreItem<'MIDDLE'>)[]
      : SubscribableCurriculumCoreItem<'MIDDLE'>[]
  }

  getUnionGradeMiddle<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getUnionGradeMiddle']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getUnionGradeMiddle(params) as T extends true
      ? (UnionGradeCurriculumCoreItem<'MIDDLE'> | AllTypeCurriculumCoreItem)[]
      : UnionGradeCurriculumCoreItem<'MIDDLE'>[]
  }

  getUnionGradeSemesterMiddle<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getUnionGradeSemesterMiddle']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getUnionGradeSemesterMiddle(params) as T extends true
      ? (UnionGradeCurriculumCoreItem<'MIDDLE'> | AllTypeCurriculumCoreItem)[]
      : UnionGradeCurriculumCoreItem<'MIDDLE'>[]
  }

  // 고등학교 ======================================================================================================
  getGradeSubjectHigh<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getGradeSubjectHigh']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getGradeSubjectHigh(params) as T extends true
      ? (AllTypeCurriculumCoreItem | SubscribableCurriculumCoreItem<'HIGH'>)[]
      : SubscribableCurriculumCoreItem<'HIGH'>[]
  }

  getUnionGradeHigh<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getUnionGradeHigh']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._curriculum.getUnionGradeHigh(params) as T extends true
      ? (AllTypeCurriculumCoreItem | UnionGradeCurriculumCoreItem<'HIGH'>)[]
      : UnionGradeCurriculumCoreItem<'HIGH'>[]
  }

  getNonStandardGradeSubjectHigh(
    params: Parameters<BaseCurriculumCoreService['getNonStandardGradeSubjectHigh']>[0],
  ): SubscribableCurriculumCoreItem<'HIGH'>[] {
    return this._curriculum.getNonStandardGradeSubjectHigh(params)
  }

  // ==============================================================================================================
  static getDefaultItem<
    CurriculumT extends ExpandedGradeSemesterSubject = ExpandedGradeSemesterSubject,
  >({
    curriculums,
    defaultSchool,
    priorityRevision,
  }: {
    curriculums: CurriculumT[]
    defaultSchool?: SchoolType
    priorityRevision?: Revision
  }): CurriculumT | undefined {
    if (!curriculums.length) {
      console.error('curriculums is empty')
    }

    const _getDefaultItem = () => {
      const revisionFilter = (revision: Revision | undefined) =>
        priorityRevision ? revision === priorityRevision : true

      const _item = curriculums.find(({ schoolType, subscribeStatus, revision }) => {
        if (defaultSchool) {
          return (
            schoolType === defaultSchool &&
            subscribeStatus === 'SUBSCRIBED' &&
            revisionFilter(revision)
          )
        }
        return subscribeStatus === 'SUBSCRIBED' && revisionFilter(revision)
      })
      if (_item) {
        return _item
      } else {
        console.error('curriculums not found')
      }
    }

    return _getDefaultItem()
  }
  // 특정 도메인, 페이지 혹은 두곳 이상 사용되는 경우 ======================================================================================================
  static makeGradeSemesterMapBasedSchools(
    schools?: SubscribableCurriculumCoreSchool[],
    opts?: {
      elementary?: SubscribableCurriculumCoreItem<'ELEMENTARY'>[]
      middle?: SubscribableCurriculumCoreItem<'MIDDLE'>[]
      high?: SubscribableCurriculumCoreItem<'HIGH'>[]
    },
  ) {
    const _schools = schools ?? curriculumCoreService.getSchools({ includeAll: false })
    const {
      elementary = curriculumCoreService.getGradeSemesterElementary({ includeAll: false }),
      middle = curriculumCoreService.getGradeSemesterMiddle({ includeAll: false }),
      high = curriculumCoreService.getGradeSubjectHigh({ includeAll: false }),
    } = opts ?? {}

    const map = new Map<
      SchoolType,
      {
        trieKeys: TrieKey[]
        grade: Map<TrieKey, CoreItem>
      }
    >()
    _schools.forEach(({ value: schoolType }) => {
      const items: CoreItem[] =
        schoolType === 'ELEMENTARY' ? elementary : schoolType === 'MIDDLE' ? middle : high
      map.set(schoolType, {
        trieKeys: curriculumCoreService.getSchoolsTrieKeys(schoolType),
        grade: new Map(items.map((item): [TrieKey, CoreItem] => [item.trieKey, item])),
      })
    })
    return map
  }

  /** 학교 타입 안의 trieKey로 맵핑된 학년-학기 정보 {"ELEMENTARY": {trieKey1: {id: 1, trieKey: '1.1.1.1', revision: 'CURRICULUM_15'}...}, "MIDDLE": {...},  "HIGH": {...}} */
  // info의 convertToSchoolTypeAndTrieKeyMap 대체 해야함
  getGradeSemesterMapBySchoolType = async ({
    isSolo,
    subscribedCurriculums,
  }: {
    isSolo: boolean
    subscribedCurriculums: typeof commonRepo.permission.bySubscription.솔로_구독_커리큘럼
  }) => {
    if (!this.isInit) {
      await this.fetchSetCurriculum({
        plan: isSolo ? 'solo' : 'default',
        subscribedCurriculums,
      })
    }
    const _map = new Map<SchoolType, Map<TrieKey, CoreItem>>()
    const _schools = this.getSchools({ includeAll: false })
    _schools.forEach((item) => {
      const _value = new Map(
        this.getAllGradeSemesters({ includePreOpen: false })
          .filter((gradeSemester) => gradeSemester.schoolType === item.value)
          .map((gradeSemester): [TrieKey, CoreItem] => [gradeSemester.trieKey, gradeSemester]),
      )
      _map.set(item.value, _value)
    })
    return _map
  }

  getGradeSemesterElementaryFrom3rdGrade<T extends boolean>(
    params: Parameters<BaseCurriculumCoreService['getGradeSemesterElementary']>[0] & {
      includeAll?: T
    } = { ...defaultParams, includeAll: true as T },
  ) {
    return this.getGradeSemesterElementary<T>(params).filter(
      ({ gradeSubject, isAllType }) => Number(gradeSubject) > 2 || isAllType,
    ) as ReturnType<typeof this.getGradeSemesterElementary<T>>
  }

  // 학교별 기출 관련: 초등 미지원, 중등 및 고등과목 일부 미지원
  get schoolTestCurriculum() {
    return {
      schools: this.getSchools({ includeAll: false }).filter(
        (item): item is SubscribableCurriculumCoreSchool<'MIDDLE' | 'HIGH'> =>
          item.value !== 'ELEMENTARY',
      ),
      middle: this.getGradeSemesterMiddle({ expandedLabel: '학년·학기 전체' })
        .filter(
          ({ gradeSubject, revision, ...item }) =>
            item.isAllType ||
            (revision === 'CURRICULUM_22' && (gradeSubject === '1' || gradeSubject === '2')) ||
            (revision === 'CURRICULUM_15' && gradeSubject === '3'),
        )
        .map((item) => {
          return item.isAllType ? item : { ...item }
        }),
      high: this.getGradeSubjectHigh()
        .filter((item) => {
          if (item.revision === 'CURRICULUM_22') {
            return (
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학1_22개정 ||
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학2_22개정 ||
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_대수_22개정 ||
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분1_22개정 ||
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_확률과통계_22개정 ||
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하_22개정
            )
          }
          if (item.revision === 'CURRICULUM_15') {
            return (
              item.trieKey !== CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상 &&
              item.trieKey !== CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_하
            )
          }
          return true
        })
        .map((item) => {
          return item.isAllType ? item : { ...item }
        }),
    }
  }
  // kmm 수학경시대회
  getKmmCurriculum() {
    return [
      ...this.getUnionGradeElementary({
        includeAll: false,
      }),
      ...this.getUnionGradeMiddle({
        includeAll: false,
      }),
      ...this.getUnionGradeHigh({
        includeAll: false,
      }).filter((v) => v.gradeSubject === '1' || v.gradeSubject === '2'),
      ...this.getGradeSubjectHigh({
        includeAll: false,
      }).filter((v) => v.gradeSubject === '확률과 통계' || v.gradeSubject === '미적분'),
    ] as (
      | UnionGradeCurriculumCoreItem<'ELEMENTARY'>
      | UnionGradeCurriculumCoreItem<'MIDDLE'>
      | UnionGradeCurriculumCoreItem<'HIGH'>
      | SubscribableCurriculumCoreItem<'HIGH'>
    )[]
  }

  getWeizmannCurriculum() {
    return [
      ...this.getUnionGradeSemesterElementary({
        includeAll: false,
      }),
      ...this.getUnionGradeSemesterMiddle({
        includeAll: false,
      }),
    ]
  }

  getKmmCurriculumForExamAndCategoryWorksheet() {
    return [
      ...this.getUnionGradeElementary({
        includeAll: true,
      }),
      ...this.getUnionGradeMiddle({
        includeAll: false,
      }),
      ...this.getUnionGradeHigh({
        includeAll: false,
      }),
    ]
  }

  // kmm 수학경시대회 자동 출제 학년
  getKmmAutoAssigningGrade() {
    return [
      ...this.getUnionGradeElementary({
        includeAll: false,
      }),
      ...this.getUnionGradeMiddle({
        includeAll: false,
      }),
    ]
  }

  /** 시험·영역별 학습지 */
  private get _highSubjectForExamAndCategoryWorksheet() {
    return this._curriculum
      .getGradeSubjectHigh({
        includeAll: false,
      })
      .filter(
        (item) =>
          item.revision === 'CURRICULUM_15' ||
          Number(item.categorizedGrade) === 1 ||
          (Number(item.categorizedGrade) === 2 &&
            (item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분1_22개정 ||
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_대수_22개정)) ||
          (Number(item.categorizedGrade) === 3 &&
            (item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하_22개정 ||
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_확률과통계_22개정 ||
              item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분2_22개정)),
      )
      .map((item) => {
        if (item.isAllType) return item
        const _item = { ...item }
        _item.labelShort =
          item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하_22개정 ||
          item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_확률과통계_22개정 ||
          item.trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분2_22개정
            ? _item.labelShort
            : removeRevisionKoFromLabel(_item.labelShort)
        return _item
      })
  }

  // 내신 대비 교과서
  get textbookWorksheet() {
    return {
      middle: this._curriculum
        .getGradeMiddle({
          includeAll: false,
        })
        .filter(
          (item) =>
            item.revision === 'CURRICULUM_15' ||
            Number(item.gradeSubject) === 1 ||
            Number(item.gradeSubject) === 2,
        ),
      high: this._highSubjectForExamAndCategoryWorksheet,
    }
  }
  // 내신 대비 추천
  get recommend() {
    const allItem = this._curriculum.getAllTypeItem({
      expandedLabel: '전체',
    })
    return {
      all: allItem ? [allItem] : [],
      middle: [
        ...this.getUnionGradeMiddle({
          includeAll: false,
        }),
      ],
      high: this._highSubjectForExamAndCategoryWorksheet,
    }
  }
  // 내신 대비 서술형
  get essayWorksheet() {
    const allItem = this._curriculum.getAllTypeItem({
      expandedLabel: '전체',
    })
    return {
      all: allItem ? [allItem] : [],
      // 내신 대비 서술형 - 초등은 2026.10 기준 3~6학년만 준비된 콘텐츠입니다. 추후 제공 콘텐츠가 추가되면 수정이 필요할 수 있습니다.
      elementary: this.getUnionGradeElementary({
        includeAll: false,
      }).filter((item) => Number(item.gradeSubject) >= 3),
      middle: [
        ...this.getUnionGradeMiddle({
          includeAll: false,
        }),
      ],
      // 내신 대비 서술형 - 고등 과목의 경우 2026.1 기준 공통수학1, 공통수학2만 준비된 콘텐츠입니다. 추후 제공 콘텐츠가 추가되면 수정이 필요할 수 있습니다.
      high: this._highSubjectForExamAndCategoryWorksheet.filter(
        (item) => item.gradeSubject === '공통수학2' || item.gradeSubject === '공통수학1',
      ),
    }
  }

  // 수능 모의고사 & 모의고사 쌍둥이
  get practiceTest() {
    // From 추가하기
    return this.getUnionGradeHigh({ includeAll: true, expandedLabel: '학년 전체' }).map((item) => {
      if (item.isAllType) {
        return {
          ...item,
          subscribeStatus: item.subscribeStatus,
          gradeSubject: 'ALL',
        }
      }
      return item
    })
  }
  // 입학TEST
  get entranceTestSubject() {
    const allItem = this._curriculum.getAllTypeItem({
      expandedLabel: '전체',
    })
    return {
      all: allItem ? [allItem] : [],
      elementary: [
        ...this._curriculum.getUnionGradeElementary({
          includeAll: false,
        }),
      ],
      middle: [
        ...this.getUnionGradeMiddle({
          includeAll: false,
        }).filter((item) => Number(item.gradeSubject) < 3),
        ...this.getGradeMiddle({
          includeAll: false,
          revisions: ['CURRICULUM_15'],
        }).filter((item) => Number(item.gradeSubject) === 3),
      ],
      high: this.getUnionGradeHigh({
        includeAll: false,
      }).filter((item) => Number(item.gradeSubject) === 1),
    }
  }
  // 주간TEST
  get weeklyTestSubject() {
    const allItem = this._curriculum.getAllTypeItem({
      expandedLabel: '전체',
    })
    return {
      all: allItem ? [allItem] : [],
      elementary: this._curriculum.getUnionGradeElementary({
        includeAll: false,
      }),
      middle: this.getUnionGradeMiddle({
        includeAll: false,
      }),
    }
  }
  // 단원TEST, 총괄TEST
  get chapterTestSubject() {
    const allItem = this._curriculum.getAllTypeItem({
      expandedLabel: '전체',
    })
    return {
      all: allItem ? [allItem] : [],
      elementary: this._curriculum.getUnionGradeElementary({
        includeAll: false,
      }),
      middle: this.getUnionGradeMiddle({
        includeAll: false,
      }),
      high: this.getUnionGradeHigh({
        includeAll: false,
      }).filter((item) => Number(item.gradeSubject) === 1),
    }
  }

  static toKorRevision(revision?: Revision): string {
    if (!revision) return ''
    const m = revision.match(/_(\d{2})$/)
    const year = m?.[1]
    if (!year || year === '15') return ''
    return `${year}개정`
  }
}

export const curriculumCoreService = new CurriculumCoreService()
